import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";

import { trackEventOncePerSession } from "@/utils/trackEvent";

import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorView } from "@tiptap/pm/view";

export type TrackUserInputOptions = {
  doc: DocumentModel;
};

function isInTitle(view: EditorView): boolean {
  const { from } = view.state.selection;
  const titleNode = view.state.doc.firstChild;
  if (titleNode?.type.name !== "documentTitle") return false;
  return from <= titleNode.nodeSize;
}

/**
 * TipTap extension that tracks user input events:
 * - document_title_edited: when the user edits the document title (once per session)
 * - text_typed: when the user types text in the editor body (once per session)
 *
 * Both events can fire in the same session, but each only fires once.
 * Uses ProseMirror props handlers to detect actual user input only.
 */
export const TrackUserInput = Extension.create<TrackUserInputOptions>({
  name: "trackUserInput",

  addProseMirrorPlugins() {
    const { doc } = this.options;

    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            beforeinput(view) {
              if (isInTitle(view)) {
                trackEventOncePerSession("document_title_edited", { doc_id: doc.id }, doc.id);
              } else {
                trackEventOncePerSession("text_typed", { doc_id: doc.id }, doc.id);
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});
